import type { Security } from '../types/Security';
import type { IOMSExecutionReport4203LegSummary } from '../types/types';
import { ProductTypeEnum, SyntheticProductTypeEnum } from '../types/types';
import { includesEnumValue } from './enumlikeHandlers';
import { getCumAmt, getFilledBaseQuantity, getFilledNotional } from './notional';
import { isOptionStrategy, isSpot } from './security';
import type { RequiredProperties } from './types';

export const shouldDisplayReasonabilityCheckForProduct = (security: Security | undefined) => {
  if (!security) {
    return false;
  }
  const EXCLUDED_PRODUCT_TYPES = [
    ProductTypeEnum.Basis,
    ProductTypeEnum.Option,
    ProductTypeEnum.CalendarSpread,
    ProductTypeEnum.PredictionOutcome,
  ] as const;
  if (includesEnumValue(EXCLUDED_PRODUCT_TYPES, security.ProductType)) {
    return false;
  }
  if (isOptionStrategy(security)) {
    return false;
  }
  if (
    hasSyntheticProductType(security) &&
    security!.MultilegDetails!.SyntheticProductType === SyntheticProductTypeEnum.Delta1Spread
  ) {
    return false;
  }
  return true;
};

export const hasSyntheticProductType = (
  security: Security | undefined
): security is RequiredProperties<Security, 'MultilegDetails'> => {
  return !!security?.MultilegDetails?.SyntheticProductType;
};

export function getLegFilledQtyAndNotional(leg: IOMSExecutionReport4203LegSummary, security: Security | undefined) {
  // Currently no countercurrency trades on multileg child orders.
  const legCurrency = security?.PositionCurrency || '';
  const cumAmt = getCumAmt(leg.CumQty || '0', leg.AvgPx || '0', legCurrency, security);
  const filledQty = getFilledBaseQuantity(
    { CumQty: leg.CumQty || '0', CumAmt: cumAmt.value, Currency: legCurrency, AmountCurrency: cumAmt.currency },
    security
  );
  const filledNotional = getFilledNotional(
    { CumQty: leg.CumQty || '0', CumAmt: cumAmt.value, Currency: legCurrency, AmountCurrency: cumAmt.currency },
    security
  );

  return {
    filledQty,
    filledNotional,
  };
}

export function areBothLegsSpot(security: Security, securitiesBySymbol: Map<string, Security>) {
  const leg1Symbol = security?.MultilegDetails?.Legs.at(0)?.Symbol;
  const leg2Symbol = security?.MultilegDetails?.Legs.at(1)?.Symbol;
  if (!leg1Symbol || !leg2Symbol) {
    return false;
  }
  return isSpot(securitiesBySymbol.get(leg1Symbol)) && isSpot(securitiesBySymbol.get(leg2Symbol));
}

export const PRODUCT_TYPE_TOOLTIP_MAP = {
  [SyntheticProductTypeEnum.Unknown]: 'unknown',
  [SyntheticProductTypeEnum.Cross]: 'Synthetic Cross',
  [SyntheticProductTypeEnum.Delta1Spread]: 'Delta-1 Spread',
  [SyntheticProductTypeEnum.CallCalendarSpread]: 'Call Calendar',
  [SyntheticProductTypeEnum.PutCalendarSpread]: 'Put Calendar',
  [SyntheticProductTypeEnum.CallSpread]: 'Call Spread',
  [SyntheticProductTypeEnum.PutSpread]: 'Put Spread',
};
